import { normalizeAdditionalInfo } from '@brands/Utils/additionalInfo';

import { client } from '../client';
import { CaseIntakeQuestions } from './createNewCase';
import { ICase } from './types/ICase';

export interface FollowUpOptionsResponse {
  case_id: number;
  allowed_case_types: string[];
}

export interface CreateLinkedCasePayload {
  case_type: string;
  assign_to_me: boolean;
  // Inline "Case details" captured in the follow-up picker.
  type?: string;
  urgency?: string;
  description?: string;
}

export interface LinkedCaseResponse {
  linked_case_id: number;
  case_journey_id: number | null;
  journey_sequence_order: number | null;
}

// The deferred consultation follow-up selection saved on the parent case
// (via the pending-linked-case endpoints). It is only materialized into a
// real linked case when the consultation report is submitted (case
// completed), so the endpoint echoes the saved selection, not a created id.
export interface PendingLinkedCaseResponse {
  case_id: number;
  pending_linked_case_type: string | null;
  pending_linked_case_assign_to_me: boolean | null;
  pending_follow_up_type: string | null;
  pending_follow_up_urgency: string | null;
  pending_follow_up_description: string | null;
  follow_up_addressed: boolean;
}

export interface JourneyCase {
  id: number;
  type: string;
  status: string;
  created_at: string;
  completed_at?: string | null;
  video_conference_date?: string | null;
  journey_sequence_order?: number | null;
}

export interface JourneyGroup {
  case_journey_id: number;
  started_at: string | null;
  status: JourneyStatus;
  cases: JourneyCase[];
}

export interface CaseJourneysResponse {
  multi_stage: JourneyGroup[];
  standalone: JourneyCase[];
}

export interface ConvertToJourneyResponse {
  case_id: number;
  case_journey_id: number | null;
}

// Derived-or-stored status of a multi-stage program (journey). `complete` and
// `in_progress` are derived from member cases when the program is neither
// paused nor canceled; `paused`/`canceled` are explicit staff actions.
export type JourneyStatus = 'in_progress' | 'paused' | 'complete' | 'canceled';

// Staff-driven program status change (pause / resume / cancel a program).
export type JourneyStatusAction = 'paused' | 'in_progress' | 'canceled';

export interface JourneyStatusResponse {
  case_journey_id: number;
  status: JourneyStatus;
}

// Staff-driven case status override used from the profile Cases view.
export type CaseStatusAction = 'completed' | 'in_progress' | 'canceled';

export interface CaseStatusResponse {
  case_id: number;
  status: string;
}

// DO-3898: allowed follow-up case types for the current case.
export const getFollowUpOptions = async (caseId: number): Promise<FollowUpOptionsResponse> => {
  const { data } = await client.get<FollowUpOptionsResponse>(`/cases/${caseId}/follow-up-options`);
  return data;
};

// Create a follow-up case linked to this case's journey IMMEDIATELY (the
// collector-profile "add case to journey" flow). Distinct from the deferred
// consultation flow below.
export const createLinkedCase = async (
  caseId: number,
  payload: CreateLinkedCasePayload,
): Promise<LinkedCaseResponse> => {
  const { data } = await client.post<LinkedCaseResponse>(`/cases/${caseId}/linked-cases`, payload);
  return data;
};

// Save (or overwrite) the deferred consultation follow-up selection on this
// case. A single slot per case enforces one follow-up at a time; re-sending
// edits the selection. No case is created and no patient notification is
// sent until the consultation report is submitted.
export const savePendingLinkedCase = async (
  caseId: number,
  payload: CreateLinkedCasePayload,
): Promise<PendingLinkedCaseResponse> => {
  const { data } = await client.put<PendingLinkedCaseResponse>(`/cases/${caseId}/pending-linked-case`, payload);
  return data;
};

// Remove the deferred follow-up selection from this case (the "remove"
// action before the report is submitted). Also clears follow_up_addressed
// so the provider must choose again.
export const deletePendingLinkedCase = async (caseId: number): Promise<PendingLinkedCaseResponse> => {
  const { data } = await client.delete<PendingLinkedCaseResponse>(`/cases/${caseId}/pending-linked-case`);
  return data;
};

export interface FollowUpDecisionResponse {
  case_id: number;
  follow_up_addressed: boolean;
}

// Record that the provider made a follow-up decision (e.g. no additional case needed).
export const recordFollowUpDecision = async (
  caseId: number,
  noAdditionalCaseNeeded: boolean,
): Promise<FollowUpDecisionResponse> => {
  const { data } = await client.post<FollowUpDecisionResponse>(`/cases/${caseId}/follow-up-decision`, {
    no_additional_case_needed: noAdditionalCaseNeeded,
  });
  return data;
};

// DO-3883 / DO-3897: multi-stage journeys + standalone cases for a patient.
export const getCaseJourneys = async (patientId: number): Promise<CaseJourneysResponse> => {
  const { data } = await client.get<CaseJourneysResponse>(`/cases/journeys?patient_id=${patientId}`);
  return data;
};

// DO-3897: convert a standalone case into a multi-stage journey.
export const convertCaseToJourney = async (caseId: number): Promise<ConvertToJourneyResponse> => {
  const { data } = await client.post<ConvertToJourneyResponse>(`/cases/${caseId}/convert-to-journey`, {});
  return data;
};

// Pause / resume / cancel a multi-stage program. Canceling cancels every open
// member case (and notifies the patient) server-side.
//
// Not idempotent server-side (re-canceling an already-canceled journey
// errors), so the global axios-retry policy (which treats all PUTs as safe
// to replay on 5xx) must be disabled per-request here: a slow-but-successful
// call would otherwise get silently retried, hit the now-canceled record,
// and surface a false error even though the original request succeeded.
export const updateJourneyStatus = async (
  journeyId: number,
  status: JourneyStatusAction,
): Promise<JourneyStatusResponse> => {
  const { data } = await client.put<JourneyStatusResponse>(
    `/cases/journeys/${journeyId}/status`,
    { status },
    { 'axios-retry': { retries: 0 } },
  );
  return data;
};

// Staff override of a single case's status (mark complete / in progress /
// cancel). Canceling routes through the same path as the cancel-case action,
// so the patient is notified. See updateJourneyStatus above for why retry is
// disabled here too.
export const updateCaseStatus = async (caseId: number, status: CaseStatusAction): Promise<CaseStatusResponse> => {
  const { data } = await client.put<CaseStatusResponse>(
    `/cases/${caseId}/case-status`,
    { status },
    { 'axios-retry': { retries: 0 } },
  );
  return data;
};

export interface IntakeQuestionConfig {
  question_key: string;
  label: string;
  answer_type: string;
  options?: any;
  is_required: boolean;
  required_rule?: any;
  is_reusable_across_journey: boolean;
  display_order: number;
}

export interface IntakeConfigResponse {
  case_type: string;
  questions: IntakeQuestionConfig[];
}

// DO-3603: fetch the data-driven intake question config for a case type.
export const getIntakeConfig = async (caseType: string): Promise<IntakeConfigResponse> => {
  const { data } = await client.get<IntakeConfigResponse>(`/cases/intake-config?case_type=${caseType}`);
  return data;
};

export interface CaseIntakeState {
  case_id: number;
  case_type: string;
  questions: IntakeQuestionConfig[];
  answers: Record<string, any>;
  outstanding: string[];
  needs_intake: boolean;
  // Only actual CC follow-ups are bundled; a root CC may still owe payment.
  is_linked_care_coordination?: boolean;
  // The case's current Status ('upcoming' while it's still waiting on the
  // patient) and whether payment specifically is part of what it's waiting
  // on. Prefer this over deriving "needs payment" from pricing.amount > 0:
  // a care-coordination follow-up bundled into a clinical case's cost is
  // free despite having a nonzero list price, and only the backend knows
  // that exception.
  status: string;
  upcoming_payment_required: boolean;
}

// Intake state of an EXISTING case (a follow-up case created on the
// patient's behalf): configured questions, current answers (including ones
// carried forward from earlier cases in the journey) and what's still
// required.
export const getCaseIntake = async (caseId: number): Promise<CaseIntakeState> => {
  const { data } = await client.get<CaseIntakeState>(`/cases/${caseId}/intake`);
  return data;
};

// Submit intake answers for an existing case. The submission must leave no
// required question unanswered; payment is attached separately via
// PATCH /cases/{id}/payment_profile.
export const submitCaseIntake = async (caseId: number, answers: Record<string, any>): Promise<CaseIntakeState> => {
  const { data } = await client.put<CaseIntakeState>(`/cases/${caseId}/intake`, { answers });
  return data;
};

// Unlike POST /cases (which takes a plain string and wraps it server-side),
// PATCH /patients/cases/{id} reuses the case's response shape for
// case_intake_questions — additional_info is a list of
// {additional_info, created_at} entries, so the client must already send it
// in that form. One entry, matching what the backend itself would have
// stored for a single note.
const normalizeCaseIntakeQuestionsPayload = (caseIntakeQuestions: CaseIntakeQuestions): Record<string, unknown> => {
  const { additional_info, ...rest } = caseIntakeQuestions;
  const normalizedNote = normalizeAdditionalInfo(additional_info);
  return {
    ...rest,
    additional_info: normalizedNote
      ? [{ additional_info: normalizedNote, created_at: new Date().toISOString() }]
      : null,
  };
};

// Complete a linked (multi-stage) case's legacy case_intake_questions after
// case creation — the counterpart to submitCaseIntake (data-driven intake)
// for case types still on the older case_intake_questions shape (one_report,
// second_opinion, video/phone call, oral_health_coaching). Used when
// completing an existing upcoming linked case through the intake wizard
// instead of creating a new one (see completeLinkedCase.ts); payment is
// attached separately via PATCH /cases/{id}/payment_profile, same as above.
// Goes through the same patient-scoped PATCH /patients/cases/{id} as
// updateCaseInsurance below — the generic staff PATCH /cases/{id} requires
// Permission.UpdateCase, which patients don't have.
export const updateCaseIntakeQuestions = async (
  caseId: number,
  caseIntakeQuestions: CaseIntakeQuestions,
): Promise<ICase> => {
  const { data } = await client.patch<ICase>(`/patients/cases/${caseId}`, {
    case_intake_questions: normalizeCaseIntakeQuestionsPayload(caseIntakeQuestions),
  });
  return data;
};

export interface UpdateCaseInsurancePayload {
  // Omit a field to leave it unchanged; pass null to clear it.
  insurance_id?: number | null;
  second_insurance_id?: number | null;
}

// Attach or clear insurance on an existing new/upcoming case (DO-4518) — the
// insurance counterpart to updatePaymentProfile, for a linked case whose
// pricing accepts insurance instead of (or before) a card. PATCH /cases/{id}
// (the staff endpoint) requires Permission.UpdateCase, which patients don't
// have; this dedicated patient-scoped route accepts only insurance_id,
// second_insurance_id, custom_carrier and case_intake_questions, restricted
// to the patient's own or their dependent's case.
export const updateCaseInsurance = async (caseId: number, payload: UpdateCaseInsurancePayload): Promise<ICase> => {
  const { data } = await client.patch<ICase>(`/patients/cases/${caseId}`, payload);
  return data;
};

// Merges the insurance attach with the legacy case_intake_questions update
// into ONE PATCH /patients/cases/{id} call instead of two round trips, since
// both already hit the same route (unlike updatePaymentProfile, a card
// payment, which lives at the separate /cases/{id}/payment_profile route and
// can't be merged in the same way).
export const updateCaseInsuranceAndIntake = async (
  caseId: number,
  insurance: UpdateCaseInsurancePayload,
  caseIntakeQuestions?: CaseIntakeQuestions,
): Promise<ICase> => {
  const { data } = await client.patch<ICase>(`/patients/cases/${caseId}`, {
    ...insurance,
    ...(caseIntakeQuestions && { case_intake_questions: normalizeCaseIntakeQuestionsPayload(caseIntakeQuestions) }),
  });
  return data;
};

export type { ICase };

// Only consultation-visible siblings, scoped to the current viewer.
export interface LinkedCasesResponse {
  case_id: number;
  case_journey_id: number | null;
  linked_cases: { id: number; type: string }[];
}

export const getLinkedCases = async (caseId: number): Promise<LinkedCasesResponse> => {
  const { data } = await client.get<LinkedCasesResponse>(`/cases/${caseId}/linked-cases`);
  return data;
};
